import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { AppTasksService } from '../app-tasks.service';
import { AppTask, AppTaskDTO, AppTaskType } from 'src/app/_model/app-task';
import { CommunicationService } from 'src/app/_services/communication/logic/commumication.service';
import {
  EventService,
  IMSEvent,
  IMSEventContentTypeHook,
  IMSEventMessageType,
  IMSEventSourceType,
  IMSInternalEventHook,
} from 'src/app/_services/event/event.service';
import { IMSEventListener } from 'src/app/_services/event/event-listener';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { IMSAction } from 'src/app/_model/menu';
import { IMSObject } from 'src/app/_model/ims-object';
import { LoadingService } from 'src/app/_services/loading/loading.service';
import { Mutex } from 'async-mutex';
import { APIService } from 'src/app/_services/api/api.service';
import { LBSUtils } from 'src/app/_util/lbs-utils';

/**
 * Loads App Parameters
 */
@Injectable()
export class AppTasksLogicService implements AppTasksService, IMSEventListener {
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  private executeMutex = new Mutex();

  private executeActionFn: (action: IMSAction) => Promise<
    | boolean
    | void
    | {
        [key: string]: any;
      }
  >;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private authenticationService: AuthenticationService,
    private communicationService: CommunicationService,
    private eventService: EventService,
    private appParametersService: AppParameterService,
    private loadingService: LoadingService,
    private apiService: APIService
  ) {
    this.initialize();
  }

  // IMSEventListener
  public async processEvent(event: IMSEvent): Promise<boolean> {
    this.logger.warn(
      this.constructor.name,
      'processEvent ',
      event.source, event.message,
      '- executeMutex locked',
      this.executeMutex.isLocked()
    );

    if (event.source.type !== IMSEventSourceType.INTERNAL) {
      return false;
    }

    if (event.source.contentType.kind !== IMSEventMessageType.HOOK) {
      return false;
    }

    const eventSource = event.source.contentType as IMSEventContentTypeHook;
    const appParams = this.appParametersService.appParameters$.getValue();

    if (appParams == null) {
      this.logger.warn(
        this.constructor.name,
        'App Parameters not available while processing event',
        eventSource.hook
      );
      return false;
    }

    this.logger.log(
      this.constructor.name,
      'received event for hook',
      IMSInternalEventHook[eventSource.hook]
    );
    // Check if hook is active otherwise abort processing of event
    let priority = 0;
    let forceBlocking = false;
    switch (eventSource.hook) {
      case IMSInternalEventHook.INITIALIZED:
        priority = appParams?.['imsmc.apptasks.check-on-hook.initialized'] ?? 0;
        break;
      case IMSInternalEventHook.SOD:
        forceBlocking =
          appParams.getBooleanParameter(
            'imsmc.apptasks.force-blocking-hook.start-of-day'
          ) ?? true;
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-day'] ?? 0;
        break;
      case IMSInternalEventHook.SOW:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-work'] ?? 0;
        break;
      case IMSInternalEventHook.EOW:
        priority = appParams?.['imsmc.apptasks.check-on-hook.end-of-work'] ?? 0;
        break;
      case IMSInternalEventHook.SOP:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-pause'] ?? 0;
        break;
      case IMSInternalEventHook.EOP:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.end-of-pause'] ?? 0;
        break;
      case IMSInternalEventHook.SOWAIT:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-wait'] ?? 0;
        break;
      case IMSInternalEventHook.EOWAIT:
        priority = appParams?.['imsmc.apptasks.check-on-hook.end-of-wait'] ?? 0;
        break;
      case IMSInternalEventHook.SOSJOB:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-servicejob'] ?? 0;
        break;
      case IMSInternalEventHook.EOSJOB:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.end-of-servicejob'] ?? 0;
        break;
      case IMSInternalEventHook.SOTRAVEL:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.start-of-travel'] ?? 0;
        break;
      case IMSInternalEventHook.EOTRAVEL:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.end-of-travel'] ?? 0;
        break;
      case IMSInternalEventHook.RARTRANSMISSION:
        priority =
          appParams?.['imsmc.apptasks.check-on-hook.rar-transmitted'] ?? 0;
        break;
      case IMSInternalEventHook.INTERNETCONNECTIONCHANGE:
        priority =
          appParams?.[
            'imsmc.apptasks.check-on-hook.internetconnectionchange'
          ] ?? 0;
        break;
      case IMSInternalEventHook.DRIVERPASSENGERIDENTIFICATION:
        priority =
          appParams?.[
            'imsmc.apptasks.check-on-hook.driverpassengeridentification'
          ] ?? 0;
        break;
      default:
        this.logger.warn(
          this.constructor.name,
          'Unknown hook event:',
          eventSource.hook,
          'abort processing'
        );
        return false;
    }

    if (priority <= 0) {
      this.logger.log(
        this.constructor.name,
        'Hook is not active:',
        eventSource.hook,
        'priority:',
        priority
      );
      return false;
    }

    // --- Slow path: kick off without blocking the event bus ---
    this.logger.warn(
      this.constructor.name,
      'processEvent - scheduling processAppTasks, executeMutex locked:',
      this.executeMutex.isLocked()
    );

    // Fire-and-forget: the mutex still serialises concurrent executions,
    // but we no longer make the EventService wait for completion.
    this.executeMutex
      .runExclusive(() => this.processAppTasks(priority, forceBlocking))
      .catch((e) =>
        this.logger.error(
          this.constructor.name,
          'Unhandled error in processAppTasks',
          e?.message
        )
      );

    return true; // event is claimed; processing happens in the background
  }
  public async notifyProcessedEventByOtherListener(): Promise<void> {}

  // Resetable
  public async reset() {}

  public async clear() {}

  // InitializeAware
  public async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      // await this.loadLocalAppTasks();
      this.logger.log(this.constructor.name, '- Initialize AppTaskLogic');
      this.eventService.registerListener(this, 0);
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  public async initializeCompleted(): Promise<void> {}

  // Public Functions

  public async logMultiStepTask(
    logType: 'starttask' | 'completedtask',
    syncType: AppTaskType,
    apiLevel: APILevel,
    syncKey: string
  ): Promise<void> {
    if (apiLevel < APILevel.APPTASKS) {
      this.logger.log(
        this.constructor.name,
        '- logSyncCycle skipped - backend does not yet support AppTasks -',
        logType
      );
      return;
    }

    try {
      this.logger.log(
        this.constructor.name,
        '- logSyncCycle started -',
        logType
      );
      let no_op_ra = new RemoteAction({
        authenticationRequired: true,
        cachable: false,
        fifo: false,
        endpoint: logType,
        data: {
          synctype: AppTaskType[syncType],
          synckey: syncKey,
        },
        httpMethod: 'POST',
      });
      this.logger.log(
        this.constructor.name,
        '- logSyncCycle generate Request -',
        logType
      );
      let request = await this.communicationService.getServerRequest(no_op_ra);

      this.logger.log(
        this.constructor.name,
        '- logSyncCycle send api call -',
        logType
      );

      apiLevel = await this.communicationService.sendRequest(
        request.url,
        request.headers,
        request.body,
        request.httpMethod
      );

      this.logger.log(
        this.constructor.name,
        '- logSyncCycle received result -'
      );
    } catch (e) {
      this.logger.warn(
        this.constructor.name,
        '- logSyncCycle received an error -',
        logType,
        syncType,
        e
      );
      throw e
    }
  }

  public setExecuteActionFunction(
    executeActionFn: (action: IMSAction) => Promise<
      | boolean
      | void
      | {
          [key: string]: any;
        }
    >
  ) {
    this.executeActionFn = executeActionFn;
  }

  // Private Functions

  private async loadRemoteAppTasks(
    apiLevel: APILevel,
    priority: number
  ): Promise<AppTask[]> {
    if (apiLevel < APILevel.APPTASKS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load AppTasks - ignore',
        apiLevel,
        APILevel.APPTASKS
      );
      return [];
    }

    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new AppTasks from IMS');

      var tasks: AppTask[] = [];
      try {
        let data: AppTaskDTO[] =
          await this.remoteActionService.sendRemoteAction(
            new RemoteAction({
              endpoint: 'loadapptasks',
              cachable: false,
              fifo: false,
              authenticationRequired: true,
              data: {
                apptaskpriority: priority,
              },
            })
          );

        this.logger.log(this.constructor.name, 'Downloaded Data:', data);

        for (const taskDto of data) {
          tasks.push(AppTask.fromDto(taskDto));
        }

        this.logger.log(this.constructor.name, 'Downloaded AppTasks: ', tasks);

        return tasks;
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading AppTasks: ',
          error?.message
        );
        throw error;
      }
    }
  }

  private async processAppTasks(
    priority: number,
    forceBlocking: boolean
  ): Promise<void> {
    try {
      if (forceBlocking) {
        await this.loadingService.showLoading();
      }
      const apiLevel = await this.apiService.getBackendApi();
      let remoteTasks =
        (await this.loadRemoteAppTasks(apiLevel, priority)) ?? [];

      if (
        remoteTasks.findIndex((it) => it.type === AppTaskType.APT_SYNC_FULL) !==
        -1
      ) {
        // Remove all other Sync AppTasks and only execute full sync as it includes the others
        remoteTasks = remoteTasks.filter(
          (it) =>
            it.type !== AppTaskType.APT_SYNC_JOBS &&
            it.type !== AppTaskType.APT_SYNC_EXTFILES &&
            it.type !== AppTaskType.APT_SYNC_JOBSECTIONS &&
            it.type !== AppTaskType.APT_SYNC_PINS &&
            it.type !== AppTaskType.APT_SYNC_TAGSTATES &&
            it.type !== AppTaskType.APT_SYNC_DEFICIENCYTICKETS &&
            it.type !== AppTaskType.APT_SYNC_PERIPHERALS &&
            it.type !== AppTaskType.APT_SYNC_OIL_BRANCH &&
            it.type !== AppTaskType.APT_SYNC_OIL_SJOB &&
            it.type !== AppTaskType.APT_SYNC_ACTIVITYCONFIRMATIONS
        );
      }

      if (
        remoteTasks.findIndex((it) => it.type === AppTaskType.APT_SYNC_JOBS) !==
        -1
      ) {
        // Remove some other Sync AppTasks and only execute job sync as it includes the others
        remoteTasks = remoteTasks.filter(
          (it) =>
            it.type !== AppTaskType.APT_SYNC_PINS &&
            it.type !== AppTaskType.APT_SYNC_TAGSTATES &&
            it.type !== AppTaskType.APT_SYNC_JOBSECTIONS &&
            it.type !== AppTaskType.APT_SYNC_DEFICIENCYTICKETS &&
            it.type !== AppTaskType.APT_SYNC_EXTFILES &&
            it.type !== AppTaskType.APT_SYNC_ACTIVITYCONFIRMATIONS
        );
      }

      if (
        remoteTasks.findIndex(
          (it) => it.type === AppTaskType.APT_SYNC_DEFICIENCYTICKETS
        ) !== -1
      ) {
        // Remove some ExtFile Sync AppTasks and only execute deficiency sync as it includes the other
        remoteTasks = remoteTasks.filter(
          (it) =>
            it.type !== AppTaskType.APT_SYNC_EXTFILES &&
            it.type !== AppTaskType.APT_SYNC_ACTIVITYCONFIRMATIONS
        );
      }

      if (
        remoteTasks.findIndex(
          (it) => it.type === AppTaskType.APT_SYNC_EXTFILES
        ) !== -1
      ) {
        // The ExtFile sync also syncs the ActivityConfirmations
        remoteTasks = remoteTasks.filter(
          (it) => it.type !== AppTaskType.APT_SYNC_ACTIVITYCONFIRMATIONS
        );
      }

      remoteTasks = remoteTasks.sort((a, b) => b.urgency - a.urgency);
      this.logger.log(
        this.constructor.name,
        'Processing AppTasks:',
        remoteTasks.length
      );
      for (const task of remoteTasks) {
        this.logger.log(
          this.constructor.name,
          'Processing AppTask:',
          AppTaskType[task.type],
          task.type
        );
        const action = task.generateAction();
        if (action == null) {
          this.logger.warn(
            this.constructor.name,
            'Generated action is null - skip task execution',
            AppTaskType[task.type],
            task.type
          );
          continue;
        }
        await this.executeActionFn(task.generateAction());
      }
    } finally {
      if (forceBlocking) {
        await this.loadingService.showLoading();
      }
    }
  }
}
